import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { IconButton, InputAdornment, Tooltip } from "@mui/material";
import {
  FormTextField,
  FormTextFieldProps,
} from "components/Forms/FormTextField";
import { useCallback, useState } from "react";
import { FieldValues, useController } from "react-hook-form";

type EditablePasswordFieldProps<T extends FieldValues> = {
  mode?: "add" | "edit";
  required?: boolean;
  onEditClick?: () => void;
  onCancelEdit?: () => void;
  canViewCredentials?: boolean;
  /** Auto-complete attribute for the password field. Defaults to "new-password"
   * to disable auto-fill and password manager saving of passwords.
   * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
   */
  autoComplete?: "new-password" | "off" | "on" | string;
} & FormTextFieldProps<T>;

export const EditablePasswordField = <T extends FieldValues>({
  mode,
  required,
  canViewCredentials = false,
  onCancelEdit,
  autoComplete = "new-password",
  ...props
}: EditablePasswordFieldProps<T>) => {
  const { onEditClick, control, name } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [revealPassword, setRevealPassword] = useState(false);

  // Use useController to get the field value
  const { field } = useController({
    name: name!,
    control,
  });

  const hasValue = Boolean(field.value);

  const handleEditClick = useCallback(() => {
    if (onEditClick) {
      onEditClick();
    }
    setIsEditing(true);
  }, [onEditClick]);

  const toggleVisibility = useCallback(() => {
    setRevealPassword(!revealPassword);
  }, [revealPassword]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setRevealPassword(false);
    if (onCancelEdit) {
      onCancelEdit();
    }
  }, [onCancelEdit]);

  return (
    <FormTextField
      {...props}
      type={revealPassword ? "text" : "password"}
      disabled={(mode === "edit" && !isEditing) || props.disabled}
      textFieldProps={{
        required,
        onFocus: handleEditClick,
        autoComplete,
      }}
      InputLabelProps={{
        shrink: (mode === "edit" && !isEditing) || hasValue,
      }}
      InputProps={{
        placeholder: mode === "edit" && !isEditing ? "*********" : undefined,
        endAdornment: (
          <InputAdornment position="end">
            {/* View button - available when user can view credentials OR when editing */}
            {(canViewCredentials || (mode === "edit" && isEditing)) && (
              <Tooltip
                title={revealPassword ? "Hide password" : "Show password"}
                placement="top"
                arrow
                enterDelay={500}
                enterNextDelay={500}
              >
                <IconButton
                  aria-label={
                    revealPassword ? "hide password" : "show password"
                  }
                  onClick={toggleVisibility}
                  edge="end"
                  disabled={props.disabled}
                  sx={{ mr: mode === "edit" ? 1 : 0 }}
                >
                  {revealPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </Tooltip>
            )}

            {/* Edit/Cancel button - only in edit mode */}
            {mode === "edit" &&
              (!isEditing ? (
                <Tooltip
                  title="Edit password"
                  placement="top"
                  arrow
                  enterDelay={500}
                  enterNextDelay={500}
                >
                  <IconButton
                    aria-label="toggle password edit"
                    onClick={handleEditClick}
                    edge="end"
                    disabled={props.disabled}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip
                  title="Cancel editing"
                  placement="top"
                  arrow
                  enterDelay={500}
                  enterNextDelay={500}
                >
                  <IconButton
                    aria-label="cancel password edit"
                    onClick={handleCancelEdit}
                    edge="end"
                    disabled={props.disabled}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              ))}
          </InputAdornment>
        ),
      }}
    />
  );
};
